<script lang="ts">
    import { onDestroy } from 'svelte';

    export let name: string;
    export let errors: Record<string, string[]>;
    export let label: string = '';
    export let value: string = '';
    export let options: { value: string; label: string }[] = [];
    export let placeholder: string = 'Search…';
    export let disabled: boolean = false;
    export let required: boolean = false;

    let query = '';
    let open = false;
    let inputEl: HTMLInputElement;

    errors[name] = [];

    $: selectedLabel = options.find(o => o.value === value)?.label ?? '';
    $: filtered = query
        ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
        : options;

    const validate = (val: string = value) => {
        errors[name] = [];
        if (required && !val) errors[name].push('Please select an option');
    };
    validate();

    const select = (opt: { value: string; label: string }) => {
        value = opt.value;
        query = '';
        open = false;
        validate(value);
    };

    const handleInput = (e: Event) => {
        query = (e.currentTarget as HTMLInputElement).value;
        value = '';
        open = true;
    };

    const handleFocus = () => { open = true; };

    const handleWindowClick = (e: MouseEvent) => {
        if (inputEl && !inputEl.closest('.combobox')?.contains(e.target as Node)) {
            open = false;
            query = '';
        }
    };

    onDestroy(() => { errors[name] = []; });
</script>

<svelte:window on:click={handleWindowClick} />

<div class="form-field">
    {#if label}<span class="label-text">{label}</span>{/if}
    <div class="combobox">
        <input
            type="text"
            bind:this={inputEl}
            {name}
            {disabled}
            {placeholder}
            value={open ? query : selectedLabel}
            on:input={handleInput}
            on:focus={handleFocus}
            class:error={errors[name].length > 0}
            autocomplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls="{name}-listbox"
            aria-autocomplete="list"
        />
        {#if open && filtered.length > 0}
            <ul class="combobox-list" role="listbox" id="{name}-listbox">
                {#each filtered as opt}
                    <li
                        class="combobox-option"
                        class:selected={opt.value === value}
                        role="option"
                        aria-selected={opt.value === value}
                        on:mousedown|preventDefault={() => select(opt)}
                    >
                        {opt.label}
                    </li>
                {/each}
            </ul>
        {/if}
    </div>
    <span class="field-errors">
        {#each errors[name] as err}<span>{err}</span>{/each}
    </span>
</div>

<style lang="scss">
    .combobox {
        position: relative;

        input { margin: 0; width: 100%; }
    }

    .combobox-list {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        z-index: 50;
        list-style: none;
        margin: 2px 0 0;
        padding: 0.25em 0;
        background-color: $color-header-bg;
        border: 1px solid $color-border;
        border-radius: 2px;
        max-height: 14em;
        overflow-y: auto;
    }

    .combobox-option {
        padding: 0.5em 0.75em;
        font-size: 0.95em;
        color: $color-text;
        cursor: pointer;
        transition: background-color 0.1s ease;

        &:hover, &.selected {
            background-color: $color-bg-active;
            color: $color-primary;
        }
    }
</style>
